import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { apiErrorMessage } from '@/services/apiClient';
import { useCreateReturnMutation, useReturnableLinesQuery } from '../api/returns.api';

interface ReturnDialogProps {
  orderId: string;
  orderReference: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (reference: string) => void;
}

/**
 * Taking goods back at the counter.
 *
 * Only what the customer actually has appears here, and each line stops at what is left after
 * earlier returns — so a lens still at the lab cannot be handed back, and the same frame cannot be
 * returned twice. Nothing goes on the shelf: the dialog says plainly that the goods are being
 * quarantined, because that is the difference between this screen and a stock receipt.
 */
export function ReturnDialog({
  orderId,
  orderReference,
  isOpen,
  onClose,
  onCreated,
}: ReturnDialogProps) {
  const { t } = useTranslation();

  const returnable = useReturnableLinesQuery(isOpen ? orderId : undefined);
  const create = useCreateReturnMutation(orderId);

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A dialog reopened on another order must not carry the last one's quantities.
  useEffect(() => {
    if (!isOpen) return;
    setQuantities({});
    setReason('');
    setError(null);
  }, [isOpen, orderId]);

  const lines = (returnable.data ?? []).filter((line) => line.returnableQuantity > 0);
  const selected = lines
    .map((line) => ({ orderLineId: line.orderLineId, quantity: quantities[line.orderLineId] ?? 0 }))
    .filter((line) => line.quantity > 0);

  const totalQuantity = selected.reduce((sum, line) => sum + line.quantity, 0);

  const submit = () => {
    setError(null);
    create.mutate(
      { lines: selected, ...(reason.trim() ? { reason: reason.trim() } : {}) },
      {
        onSuccess: (created) => {
          onCreated(created.reference);
          onClose();
        },
        onError: (caught) => setError(apiErrorMessage(caught) ?? t('sales.returns.errors.create')),
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sales.returns.dialogTitle', { reference: orderReference })}
      description={t('sales.returns.dialogHint')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            isLoading={create.isPending}
            disabled={totalQuantity === 0 || create.isPending}
          >
            {t('sales.returns.confirm', { count: totalQuantity })}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="error">{error}</Alert> : null}

      {returnable.isPending ? (
        <LoadingState />
      ) : lines.length === 0 ? (
        <Alert tone="info">{t('sales.returns.nothingReturnable')}</Alert>
      ) : (
        <div className="space-y-4">
          <ul className="divide-line divide-y">
            {lines.map((line) => (
              <li key={line.orderLineId} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="text-ink block truncate font-medium">{line.productName}</span>
                  <span className="text-muted font-mono text-xs" dir="ltr">
                    {line.sku}
                  </span>
                  <span className="text-muted block text-xs">
                    {t('sales.returns.deliveredOf', {
                      returnable: line.returnableQuantity,
                      delivered: line.quantityDelivered,
                    })}
                  </span>
                </span>

                <div className="w-24">
                  <Input
                    type="number"
                    min={0}
                    max={line.returnableQuantity}
                    step={1}
                    inputMode="numeric"
                    aria-label={t('sales.returns.quantityFor', { sku: line.sku })}
                    value={String(quantities[line.orderLineId] ?? 0)}
                    onChange={(event) => {
                      const raw = Number.parseInt(event.target.value, 10);
                      const next = Number.isFinite(raw) ? raw : 0;
                      setQuantities((current) => ({
                        ...current,
                        [line.orderLineId]: Math.max(0, Math.min(next, line.returnableQuantity)),
                      }));
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>

          <FormField label={t('sales.returns.reason')} hint={t('sales.returns.reasonHint')}>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={reason}
                maxLength={500}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('sales.returns.reasonPlaceholder')}
              />
            )}
          </FormField>

          <Alert tone="info">{t('sales.returns.quarantineNotice')}</Alert>
        </div>
      )}
    </Modal>
  );
}
